import { useQuery } from '@tanstack/react-query'
import { getBrands, getDeviceTypes, getLocations } from '../devices'
import { adminGetLocations } from '../organization'
import { queryKeys } from '../queryKeys'
import { adminGetBrands } from '../users'
import type { QueryGate } from './options'

/**
 * Brands, locations and device types: the lists every filter and picker reads.
 *
 * One key per list, from `queryKeys`, so the devices table, the pickers and the admin pages share a
 * cache entry and a brand renamed on one page is renamed on all of them.
 */

/** `GET /api/brands`: the brands the caller can see. */
export function useBrands({ enabled = true }: QueryGate = {}) {
  return useQuery({ queryKey: queryKeys.brands.list(), queryFn: getBrands, enabled, retry: false })
}

/** `GET /api/admin/brands`: every brand, enabled or not. */
export function useAdminBrands({ enabled = true }: QueryGate = {}) {
  return useQuery({ queryKey: queryKeys.brands.admin(), queryFn: adminGetBrands, enabled, retry: false })
}

/** `GET /api/locations`: the stores the caller can see. */
export function useLocations({ enabled = true }: QueryGate = {}) {
  return useQuery({ queryKey: queryKeys.locations.list(), queryFn: getLocations, enabled, retry: false })
}

/** `GET /api/admin/locations`, for one brand or (`''`) every brand. */
export function useAdminLocations(brand: string, { enabled = true }: QueryGate = {}) {
  return useQuery({
    queryKey: queryKeys.locations.admin(brand),
    queryFn: () => adminGetLocations(brand || undefined),
    enabled,
    retry: false,
  })
}

/** `GET /api/device-types`. */
export function useDeviceTypes({ enabled = true }: QueryGate = {}) {
  return useQuery({ queryKey: queryKeys.deviceTypes.list(), queryFn: getDeviceTypes, enabled, retry: false })
}
